import React from 'react';
import {Form, Formik} from 'formik';
import {useDispatch} from 'react-redux';
import {saveCommunityServiceEvent} from 'shared/portfolio/actions/community-service';
import {validateLength} from 'shared/utils';

interface CommonFields {
  _id: string;
  createdAt: number;
  updatedAt: number;
  description: string;
  hours: number;
  place: string;
  requiredByHs: boolean;
  startMonth: number;
  startYear: number;

  // not from the API, FE only
  editing?: boolean;
  new?: boolean;
  scrollTo?: boolean;
}

interface WithRange extends CommonFields {
  endMonth: number;
  endYear: number;

  // not from the API, FE only
  dateRange: true;
}

export interface WithoutRange extends CommonFields {
  endMonth: null;
  endYear: null;

  // not from the API, FE only
  dateRange: false;
}

export type CommunityService = WithRange | WithoutRange;

export type CommunityServiceForm = Omit<
  CommunityService,
  'startYear' | 'startMonth' | 'hours' | 'updatedAt'
> & {
  startMonth: number | null;
  startYear: number | null;
  hours: number | '';
};

interface Props {
  initialValues: CommunityServiceForm;
  children: React.ReactNode;
  onSuccess?: () => void;
}

const CommunityServiceFormWrapper = ({initialValues, children, onSuccess}: Props) => {
  const dispatch = useDispatch();

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors: Partial<Record<keyof CommunityServiceForm, string>> = {};
        const today = new Date();
        const placeValidation = validateLength('Organization name', values.place, 256);

        if (!values.place) {
          errors.place = 'Organization name is required';
        } else if (placeValidation) {
          errors.place = placeValidation;
        }

        if (!values.description) {
          errors.description = 'Description is required';
        }

        if (!values.hours) {
          errors.hours = 'Hours are required';
        }

        if (values.startMonth === null || values.startMonth < 0 || values.startMonth > 11) {
          errors.startMonth = 'Month is required';
        }

        if (!values.startYear) {
          errors.startYear = 'Year is required';
        }

        if (values.startMonth !== null && values.startYear) {
          const startDate = new Date(values.startYear, values.startMonth, today.getDate());

          if (startDate.getFullYear() > today.getFullYear()) {
            errors.startYear = 'Date must not be in the future';
          } else if (startDate > today) {
            errors.startMonth = 'Date must not be in the future';
          }
        }

        if (values.dateRange) {
          if (values.endMonth === null || values.endMonth < 0 || values.endMonth > 11) {
            errors.endMonth = 'Month is required';
          }

          if (!values.endYear) {
            errors.endYear = 'Year is required';
          }

          if (
            values.startMonth !== null &&
            values.startYear &&
            values.endMonth !== null &&
            values.endYear
          ) {
            const startDate = new Date(values.startYear, values.startMonth, today.getDate());
            const endDate = new Date(values.endYear, values.endMonth, today.getDate());

            if (endDate.getFullYear() > today.getFullYear()) {
              errors.endYear = 'Date must not be in the future';
            } else if (endDate > today) {
              errors.endMonth = 'Date must not be in the future';
            } else if (startDate.getFullYear() > endDate.getFullYear()) {
              errors.endYear = 'Start date must not be after end date';
            } else if (startDate > endDate) {
              errors.endMonth = 'Start date must not be after end date';
            }
          }
        }

        return errors;
      }}
      onSubmit={async (values, formik) => {
        try {
          await dispatch(saveCommunityServiceEvent(values));
          onSuccess?.();
        } catch (errors: $TSFixMe) {
          Object.keys(errors).forEach((field) => {
            if (errors[field]) {
              formik.setFieldError(field, errors[field]);
            }
          });
        }
      }}
    >
      <Form autoComplete="off">{children}</Form>
    </Formik>
  );
};

export default CommunityServiceFormWrapper;
